import { t } from "@/lib/i18n/interpolate";
import {
  type DocBlock,
  type DocModel,
  type DocSection,
  ensurePeriod,
  linesOf,
  listItemsOf,
} from "@/lib/tool-kit/research-workflow/document";

/**
 * A participant debriefing statement.
 *
 * Built around the two research standards of the APA Ethics Code that govern the end of
 * a study: 8.08, which asks for a prompt opportunity to learn the nature, results and
 * conclusions of the research and for reasonable steps to correct misconceptions and to
 * minimise any harm, and 8.07(c), which asks that deception be explained as early as
 * feasible — no later than the end of data collection — and that deceived participants
 * be allowed to withdraw their data.
 *
 * The order is the order a participant needs: thanks, the real purpose, what was held
 * back and why, what happens to their data, where to get support, where to read more,
 * whom to contact — and, when the researcher asks for it, a renewed choice about the
 * data now that the participant knows everything.
 */

export type WithdrawalMode = "contact" | "here" | "none";

export interface DebriefInput {
  title: string;
  researcher: string;
  institution: string;
  contact: string;
  irbName: string;
  irbContact: string;
  purpose: string;
  /** One expectation per line. */
  hypotheses: string;
  conditions: string;
  deception: boolean;
  coverStory: string;
  withheld: string;
  justification: string;
  withdrawal: WithdrawalMode;
  /** Already formatted for the reader's locale, or empty. */
  deadline: string;
  confidentiality: string;
  noDiscussion: boolean;
  /** One service per line. */
  support: string;
  /** One reference per line. */
  reading: string;
  /** Already formatted for the reader's locale, or empty. */
  resultsDate: string;
}

export const EMPTY_DEBRIEF_INPUT: DebriefInput = {
  title: "",
  researcher: "",
  institution: "",
  contact: "",
  irbName: "",
  irbContact: "",
  purpose: "",
  hypotheses: "",
  conditions: "",
  deception: false,
  coverStory: "",
  withheld: "",
  justification: "",
  withdrawal: "contact",
  deadline: "",
  confidentiality: "",
  noDiscussion: false,
  support: "",
  reading: "",
  resultsDate: "",
};

/** The statement's words. The widget's strings object satisfies this structurally. */
export interface DebriefPhrases {
  docTitle: string;
  metaStudy: string;
  metaResearcher: string;
  metaInstitution: string;
  thanksHeading: string;
  thanksPlain: string;
  thanksDeception: string;
  purposeHeading: string;
  hypothesesLead: string;
  deceptionHeading: string;
  coverLead: string;
  withheldLead: string;
  whyLead: string;
  apologySentence: string;
  dataHeading: string;
  withdrawContact: string;
  withdrawContactBy: string;
  withdrawHere: string;
  withdrawNone: string;
  discussionHeading: string;
  discussionSentence: string;
  supportHeading: string;
  supportLead: string;
  readingHeading: string;
  readingLead: string;
  resultsSentence: string;
  resultsSentenceAfter: string;
  contactHeading: string;
  contactStudy: string;
  contactRights: string;
  choiceHeading: string;
  choiceLead: string;
  choiceKeep: string;
  choiceWithdraw: string;
  missingTitle: string;
  missingResearcher: string;
  missingContact: string;
  missingIrb: string;
  missingIrbContact: string;
  missingPurpose: string;
  missingWithheld: string;
  missingJustification: string;
  missingSupport: string;
}

function or(value: string, fallback: string): string {
  const clean = value.trim();
  return clean === "" ? fallback : clean;
}

function paragraphs(text: string, fallback: string | null): DocBlock[] {
  const lines = linesOf(text);
  if (lines.length === 0) {
    return fallback ? [{ kind: "p", text: fallback }] : [];
  }
  return lines.map((line) => ({ kind: "p", text: ensurePeriod(line) }));
}

function who(input: DebriefInput, p: DebriefPhrases) {
  return {
    researcher: or(input.researcher, p.missingResearcher),
    contact: or(input.contact, p.missingContact),
  };
}

function purposeSection(input: DebriefInput, p: DebriefPhrases): DocSection {
  const blocks = paragraphs(input.purpose, p.missingPurpose);
  const hypotheses = listItemsOf(input.hypotheses);
  if (hypotheses.length > 0) {
    blocks.push({ kind: "p", text: p.hypothesesLead });
    blocks.push({ kind: "ul", items: hypotheses.map(ensurePeriod) });
  }
  blocks.push(...paragraphs(input.conditions, null));
  return { heading: p.purposeHeading, blocks };
}

function labelled(lead: string, text: string): DocBlock[] {
  const lines = linesOf(text);
  if (lines.length === 0) {
    return [];
  }
  const [first, ...rest] = lines;
  return [
    { kind: "p", text: `${lead} ${ensurePeriod(first)}` },
    ...rest.map((line): DocBlock => ({ kind: "p", text: ensurePeriod(line) })),
  ];
}

function deceptionSection(
  input: DebriefInput,
  p: DebriefPhrases
): DocSection | null {
  if (!input.deception) {
    return null;
  }
  const withheld = input.withheld.trim() ? input.withheld : p.missingWithheld;
  const why = input.justification.trim()
    ? input.justification
    : p.missingJustification;
  return {
    heading: p.deceptionHeading,
    blocks: [
      ...labelled(p.coverLead, input.coverStory),
      ...labelled(p.withheldLead, withheld),
      ...labelled(p.whyLead, why),
      { kind: "p", text: p.apologySentence },
    ],
  };
}

function withdrawalSentence(input: DebriefInput, p: DebriefPhrases): string {
  if (input.withdrawal === "here") {
    return p.withdrawHere;
  }
  if (input.withdrawal === "none") {
    return p.withdrawNone;
  }
  const { researcher, contact } = who(input, p);
  return input.deadline
    ? t(p.withdrawContactBy, { researcher, contact, date: input.deadline })
    : t(p.withdrawContact, { researcher, contact });
}

function dataSection(input: DebriefInput, p: DebriefPhrases): DocSection {
  return {
    heading: p.dataHeading,
    blocks: [
      ...paragraphs(input.confidentiality, null),
      { kind: "p", text: withdrawalSentence(input, p) },
    ],
  };
}

function supportSection(input: DebriefInput, p: DebriefPhrases): DocSection {
  const services = listItemsOf(input.support);
  const blocks: DocBlock[] = [
    { kind: "p", text: t(p.supportLead, who(input, p)) },
  ];
  blocks.push(
    services.length > 0
      ? { kind: "ul", items: services }
      : { kind: "p", text: p.missingSupport }
  );
  return { heading: p.supportHeading, blocks };
}

function readingSection(input: DebriefInput, p: DebriefPhrases): DocSection {
  const references = listItemsOf(input.reading);
  const blocks: DocBlock[] = [];
  if (references.length > 0) {
    blocks.push({ kind: "p", text: p.readingLead });
    blocks.push({ kind: "ul", items: references });
  }
  const people = who(input, p);
  blocks.push({
    kind: "p",
    text: input.resultsDate
      ? t(p.resultsSentenceAfter, { ...people, date: input.resultsDate })
      : t(p.resultsSentence, people),
  });
  return { heading: p.readingHeading, blocks };
}

function choiceSection(
  input: DebriefInput,
  p: DebriefPhrases
): DocSection | null {
  if (input.withdrawal !== "here") {
    return null;
  }
  // No name or signature: this is the option for studies whose data are anonymous,
  // and a name on the sheet would undo that.
  return {
    heading: p.choiceHeading,
    blocks: [
      { kind: "p", text: p.choiceLead },
      { kind: "fields", items: [p.choiceKeep, p.choiceWithdraw] },
    ],
  };
}

/** The whole debriefing statement, in reading order. */
export function buildDebriefing(
  input: DebriefInput,
  p: DebriefPhrases
): DocModel {
  const meta = [t(p.metaStudy, { value: or(input.title, p.missingTitle) })];
  meta.push(
    t(p.metaResearcher, { value: or(input.researcher, p.missingResearcher) })
  );
  if (input.institution.trim()) {
    meta.push(t(p.metaInstitution, { value: input.institution.trim() }));
  }
  const people = who(input, p);
  const sections: (DocSection | null)[] = [
    {
      heading: p.thanksHeading,
      blocks: [
        {
          kind: "p",
          text: input.deception ? p.thanksDeception : p.thanksPlain,
        },
      ],
    },
    purposeSection(input, p),
    deceptionSection(input, p),
    dataSection(input, p),
    input.noDiscussion
      ? {
          heading: p.discussionHeading,
          blocks: [{ kind: "p", text: p.discussionSentence }],
        }
      : null,
    supportSection(input, p),
    readingSection(input, p),
    {
      heading: p.contactHeading,
      blocks: [
        { kind: "p", text: t(p.contactStudy, people) },
        {
          kind: "p",
          text: t(p.contactRights, {
            irb: or(input.irbName, p.missingIrb),
            irbContact: or(input.irbContact, p.missingIrbContact),
          }),
        },
      ],
    },
    choiceSection(input, p),
  ];
  return {
    title: p.docTitle,
    meta,
    sections: sections.filter((s): s is DocSection => s !== null),
  };
}

/* ------------------------------------------------------------------ checklist */

export type DebriefElement =
  | "purpose"
  | "deception"
  | "withdrawal"
  | "results"
  | "support"
  | "contacts";

export interface DebriefCheck {
  element: DebriefElement;
  status: "covered" | "missing" | "standard" | "na";
}

function filled(...values: string[]): boolean {
  return values.every((value) => value.trim() !== "");
}

/**
 * The APA 8.07(c) / 8.08 items and whether the statement covers them. Withdrawal is
 * "missing" when deception was used and the researcher offers no way to withdraw — the
 * one combination 8.07(c) rules out.
 */
export function checkDebriefing(input: DebriefInput): DebriefCheck[] {
  let deception: DebriefCheck["status"] = "na";
  if (input.deception) {
    deception = filled(input.withheld, input.justification)
      ? "covered"
      : "missing";
  }
  const blockedWithdrawal = input.deception && input.withdrawal === "none";
  return [
    {
      element: "purpose",
      status: filled(input.purpose) ? "covered" : "missing",
    },
    { element: "deception", status: deception },
    {
      element: "withdrawal",
      status: blockedWithdrawal ? "missing" : "covered",
    },
    { element: "results", status: "standard" },
    {
      element: "support",
      status: listItemsOf(input.support).length > 0 ? "covered" : "missing",
    },
    {
      element: "contacts",
      status: filled(input.researcher, input.contact, input.irbContact)
        ? "covered"
        : "missing",
    },
  ];
}

/** Everything the researcher typed as prose — what the plain-language check reads. */
export function debriefProse(input: DebriefInput): string {
  return [
    input.purpose,
    input.hypotheses,
    input.conditions,
    input.coverStory,
    input.withheld,
    input.justification,
    input.confidentiality,
  ]
    .map((text) => text.trim())
    .filter((text) => text.length > 0)
    .join("\n");
}
